/* ---------------------------------------------------------------------------
   Three small forms over the rest of the site record: the daily return, an
   incident, and a document.

   ONE FILE FOR THREE, where "Record work" has a file of its own. The same
   argument functions/lib/siteRecords.ts makes on the server: these three exist
   to answer the parts of FIDIC 4.21 that a progress entry structurally cannot,
   they were built together, each is a short form, and three files would be
   three copies of the same scrim, head and footer. The work sheet is separate
   because it is the one that has to be usable one-handed in sunlight and its
   activity picker is most of its size.

   WHAT THE THREE HAVE IN COMMON, and it is the reason they are worth having at
   all: each closes a section of the monthly report that currently prints *not
   recorded*. The return closes (d), the incident closes (g), the document
   closes (c) and most of (e).

   THE DAILY RETURN IS THE ONE WITH A RULE OF ITS OWN. It is a measurement of a
   day rather than an observation of an event, so opening it on a day that
   already has a return loads that return to be corrected — and a day that has
   been accepted is frozen, which the sheet says rather than silently failing.
   --------------------------------------------------------------------------- */

import { h, ICONS, type IconPath } from '../core/dom.ts';
import {
  actions,
  type DocumentDraft,
  type IncidentDraft,
  type ReturnDraft,
  type State,
} from '../core/store.ts';
import { button, field, scrim } from '../components/widgets.ts';
import { approxKb, downscale } from '../core/downscale.ts';
import { findBridge } from '../data/derive.ts';
import { siteElementOptions } from './progressDialog.ts';
import { activityLabel } from '../data/activities.ts';
import { INCIDENT_KINDS, incidentOption, type IncidentKind } from '../data/siteReturn.ts';
import { shadedIcon } from '../components/shadedIcon.ts';
import { weatherPicker } from '../components/weather.ts';
import {
  DOCUMENT_KINDS,
  MAX_DOCUMENT_BYTES,
  isDocumentType,
} from '../data/documents.ts';

/* ===========================================================================
   THE DAILY RETURN
   =========================================================================== */

/** What the return is headed: a new day, or one already on record being corrected. */
export function siteReturnTitle(s: Readonly<State>): string {
  return s.siteReturns.some((r) => r.returnedOn === s.returnDraft.returnedOn)
    ? 'Correct the day'
    : 'Daily site return';
}

export const INCIDENT_TITLE = 'Report an incident';

/** The daily return, or null when it is closed. */
export function siteReturnDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.returnBridgeId) return null;
  const b = findBridge(s.returnBridgeId);
  if (b === null) return null;

  const d = s.returnDraft;
  const set = (key: keyof ReturnDraft) => (e: Event) =>
    actions.setReturnField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  /* Whether the day on the sheet already has a return in the register, so the
     heading can say whether this is a new day or a correction. Somebody who
     did not notice the difference would think they had lost this morning's
     figures. */
  const existing = s.siteReturns.find((r) => r.returnedOn === d.returnedOn);

  const form = h(
      'form',
      {
        ...formAttrs(),
        'aria-label': `Daily site return — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveSiteReturn();
        },
      },

      dialogHead(`${b.id} · daily return`, siteReturnTitle(s), () => actions.closeSiteReturn()),

      s.returnError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.returnError }))
        : null,

      /* NOT AN ERROR AND DRAWN DIFFERENTLY. Nothing was wrong with what was
         typed; the day has been signed off and the figures were not written.
         The sheet stays open so it is unmistakable. */
      s.returnFrozen
        ? h(
            'div',
            { class: 'dlg__note dlg__note--warn' },
            h('span', {
              text:
                'This day has already been reviewed and accepted, so it is frozen and nothing ' +
                'was written. Anything that needs changing now goes to whoever accepted it.',
            }),
          )
        : null,

      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__form-grid' },

          field(
            'Day',
            h('input', {
              id: 'ret-day',
              class: 'input',
              type: 'date',
              required: true,
              value: d.returnedOn,
              /* Its own action, not setReturnField: moving the date changes
                 which record is being written, and the sheet loads that day's
                 figures if it has any. */
              oninput: (e: Event) => actions.setReturnDay((e.target as HTMLInputElement).value),
            }),
            'ret-day',
          ),

          field(
            'People on site',
            d.trades.length
              ? /* With trades the total is their sum (LC-56), not a second figure
                   that could disagree with them. */
                h('output', { id: 'ret-labour', class: 'input', 'data-total': String(tradeTotal(d) ?? ''), text: `${tradeTotal(d) ?? '—'} · the trades’ sum` })
              : h('input', {
                  id: 'ret-labour',
                  class: 'input',
                  type: 'number',
                  min: '0',
                  step: '1',
                  inputmode: 'numeric',
                  /* "Nobody counted" and "nobody was here" are different answers
                     and the box says so: an empty field is the first and a typed
                     zero is the second. */
                  placeholder: 'Leave empty if nobody counted',
                  value: d.labour,
                  oninput: set('labour'),
                }),
            'ret-labour',
          ),

          field(
            'Rainfall (mm)',
            h('input', {
              id: 'ret-rain',
              class: 'input',
              type: 'number',
              min: '0',
              step: 'any',
              inputmode: 'decimal',
              placeholder: 'Leave empty if not measured',
              value: d.rainfall,
              oninput: set('rainfall'),
            }),
            'ret-rain',
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            /* Required, and a sky to pick rather than a list: whoever writes the
               day's return says what the weather did (see `saveSiteReturn`). */
            field(
              'Weather',
              weatherPicker(d.weather, (id) => actions.setReturnField('weather', id)),
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field('Labour by trade', tradeRows(d), 'ret-trade-0'),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Labour note',
              h('input', {
                id: 'ret-trades',
                class: 'input',
                /* Free text on purpose — this project has no FWO method
                   statement and a compiled trade list would be our guess at
                   somebody else's establishment. See the vocabulary's header. */
                placeholder: 'e.g. 6 steel fixers, 4 carpenters, 2 masons',
                value: d.labourNote,
                oninput: set('labourNote'),
              }),
              'ret-trades',
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field('Plant working and idle', plantRows(d), 'ret-plant-0'),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Plant note',
              h('input', {
                id: 'ret-plant',
                class: 'input',
                placeholder: 'e.g. 50 t crawler crane, 2 concrete pumps, 1 excavator',
                value: d.plant,
                oninput: set('plant'),
              }),
              'ret-plant',
            ),
          ),

          field(
            'Hours worked',
            h('input', {
              id: 'ret-hours',
              class: 'input',
              type: 'number',
              min: '0',
              step: 'any',
              inputmode: 'decimal',
              placeholder: 'Optional',
              value: d.hours,
              oninput: set('hours'),
            }),
            'ret-hours',
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Note',
              h('textarea', {
                id: 'ret-note',
                class: 'input',
                rows: 3,
                placeholder: 'Anything about the day the figures do not say',
                value: d.note,
                oninput: set('note'),
              }),
              'ret-note',
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            sitePhotoField(
              d.photo,
              existing?.hasPhoto === true,
              (url) => actions.setReturnPhoto(url),
              (msg) => actions.setSiteFormError('return', msg),
            ),
          ),
        ),
      ),

      dialogFoot(
        () => actions.closeSiteReturn(),
        s.returnSaving ? 'Recording…' : existing ? 'Correct the day' : 'Record the day',
        s.returnSaving,
      ),
    );
  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeSiteReturn(), () => s.returnSaving) },
    form,
  );
}

/** The people on site the trades make, or null where no trade has a number. */
export function tradeTotal(d: ReturnDraft): number | null {
  const counts = d.trades
    .filter((t) => t.count.trim() !== '')
    .map((t) => Number(t.count))
    .filter((n) => Number.isFinite(n) && n >= 0);
  return counts.length ? counts.reduce((a, b) => a + b, 0) : null;
}

/** People by trade: a row per trade, a name and a count (LC-56). */
function tradeRows(d: ReturnDraft): HTMLElement {
  return h(
    'div',
    { class: 'dayrows', 'data-rows': String(d.trades.length) },
    ...d.trades.map((row, i) =>
      h(
        'div',
        { class: 'dayrows__row', style: 'display:flex;gap:6px;margin-bottom:4px' },
        h('input', {
          id: `ret-trade-${i}`,
          class: 'input',
          placeholder: 'Trade, e.g. Steel fixers',
          value: row.trade,
          oninput: (e: Event) => actions.setReturnRow('trades', i, 'trade', (e.target as HTMLInputElement).value),
        }),
        h('input', {
          class: 'input',
          type: 'number',
          min: '0',
          step: '1',
          inputmode: 'numeric',
          style: 'max-width:90px',
          'aria-label': 'People',
          value: row.count,
          oninput: (e: Event) => actions.setReturnRow('trades', i, 'count', (e.target as HTMLInputElement).value),
        }),
        button('Remove', { sm: true, onClick: () => actions.removeReturnRow('trades', i) }),
      ),
    ),
    button('Add a trade', { sm: true, onClick: () => actions.addReturnRow('trades') }),
  );
}

/** Plant working and idle: a row per item, its hours working and standing (LC-56). */
function plantRows(d: ReturnDraft): HTMLElement {
  return h(
    'div',
    { class: 'dayrows', 'data-rows': String(d.plantRows.length) },
    ...d.plantRows.map((row, i) =>
      h(
        'div',
        { class: 'dayrows__row', style: 'display:flex;gap:6px;margin-bottom:4px' },
        h('input', {
          id: `ret-plant-${i}`,
          class: 'input',
          placeholder: 'Plant, e.g. 50 t crawler crane',
          value: row.plant,
          oninput: (e: Event) => actions.setReturnRow('plantRows', i, 'plant', (e.target as HTMLInputElement).value),
        }),
        h('input', {
          class: 'input',
          type: 'number',
          min: '0',
          max: '24',
          step: 'any',
          style: 'max-width:90px',
          'aria-label': 'Hours working',
          placeholder: 'Working h',
          value: row.working,
          oninput: (e: Event) => actions.setReturnRow('plantRows', i, 'working', (e.target as HTMLInputElement).value),
        }),
        h('input', {
          class: 'input',
          type: 'number',
          min: '0',
          max: '24',
          step: 'any',
          style: 'max-width:90px',
          'aria-label': 'Hours idle',
          placeholder: 'Idle h',
          value: row.idle,
          oninput: (e: Event) => actions.setReturnRow('plantRows', i, 'idle', (e.target as HTMLInputElement).value),
        }),
        button('Remove', { sm: true, onClick: () => actions.removeReturnRow('plantRows', i) }),
      ),
    ),
    button('Add plant', { sm: true, onClick: () => actions.addReturnRow('plantRows') }),
  );
}

/**
 * One photograph on a return or an incident (LC-56): the progress entry's
 * strip, off the rear camera and downscaled on the device. `stored` says the
 * record already holds one, which a new one replaces.
 */
function sitePhotoField(
  photo: string | null,
  stored: boolean,
  onSet: (dataUrl: string | null) => void,
  onError: (message: string) => void,
): HTMLElement {
  const input = h('input', {
    type: 'file',
    accept: 'image/*',
    capture: 'environment',
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      target.value = '';
      if (!file) return;
      void downscale(file).then(
        (shrunk) => onSet(shrunk.dataUrl),
        (err: unknown) => onError(err instanceof Error ? err.message : 'That file could not be read.'),
      );
    },
  });
  return field(
    'Photograph',
    h(
      'div',
      { class: 'insp-photos' },
      photo
        ? h(
            'span',
            { class: 'insp-photos__item' },
            h('img', { class: 'insp-photos__img', src: photo, alt: 'The photograph attached' }),
            h('span', { class: 'insp-photos__size', text: `${approxKb(photo)} kB` }),
            h('button', {
              type: 'button',
              class: 'imgslot__clear',
              title: 'Remove this photograph',
              'aria-label': 'Remove this photograph',
              text: '✕',
              onclick: () => onSet(null),
            }),
          )
        : h('label', { class: 'insp-photos__add' }, input, h('span', { text: stored ? 'Replace the photograph' : 'Take a photograph' })),
    ),
  );
}

/* ===========================================================================
   AN INCIDENT
   =========================================================================== */

const INCIDENT_ICON: Record<IncidentKind, IconPath> = {
  'near-miss': ICONS.incident,
  'first-aid': ICONS.firstAid,
  'lost-time': ICONS.lostTime,
  'dangerous-occurrence': ICONS.dangerous,
  environmental: ICONS.environmental,
};

/** A field's label with "Optional" set at its far end. */
function optionalLabel(text: string): HTMLElement {
  return h('span', { class: 'field__optlabel' }, text, h('span', { class: 'field__opt', text: 'Optional' }));
}

/** An incident, or null when none is being reported. */
export function incidentDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.incidentBridgeId) return null;
  const b = findBridge(s.incidentBridgeId);
  if (b === null) return null;

  const d = s.incidentDraft;
  const kind = incidentOption(d.kind);
  const set = (key: keyof IncidentDraft) => (e: Event) =>
    actions.setIncidentField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  const form = h(
      'form',
      {
        ...formAttrs(),
        'aria-label': `Report an incident — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveIncident();
        },
      },

      dialogHead(`${b.id} · safety`, INCIDENT_TITLE, () => actions.closeIncident()),

      s.incidentError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.incidentError }))
        : null,

      h(
        'div',
        { class: 'dlg__body' },

        /* THE KIND FIRST AND AS TILES. Five entries, and the one that matters
           most — a near miss — is the one most easily not reported at all, so
           it is a single tap and the first thing on the form. */
        h(
          'div',
          { class: 'dlg__block' },
          field(
            'What kind of thing happened',
            h(
              'div',
              { class: 'kindpick', role: 'radiogroup', 'aria-label': 'Kind of incident' },
              ...INCIDENT_KINDS.map((k) =>
                h(
                  'button',
                  {
                    type: 'button',
                    class: ['kindpick__tile', k.id === d.kind && 'kindpick__tile--on'],
                    role: 'radio',
                    'aria-checked': k.id === d.kind ? 'true' : 'false',
                    onclick: () => actions.setIncidentField('kind', k.id),
                  },
                  shadedIcon(INCIDENT_ICON[k.id], 32),
                  h('span', { text: k.label }),
                ),
              ),
            ),
          ),
        ),

        h(
          'div',
          { class: 'dlg__form-grid' },

          field(
            'When',
            h('input', {
              id: 'inc-date',
              class: 'input',
              type: 'date',
              required: true,
              value: d.occurredOn,
              oninput: set('occurredOn'),
            }),
            'inc-date',
          ),

          field(
            'Where on the structure',
            h(
              'select',
              { id: 'inc-element', class: 'select', onchange: set('element') },
              /* "Not recorded" is a real answer here more often than anywhere
                 else in this register: an incident in the site compound
                 belongs to no element of the bridge. */
              h('option', { value: '', selected: d.element === '', text: 'Not recorded' }),
              ...siteElementOptions(d.element),
            ),
            'inc-element',
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'What happened',
              h('textarea', {
                id: 'inc-desc',
                class: 'input',
                rows: 4,
                required: true,
                placeholder: 'What happened, to whom or to what, and how',
                value: d.description,
                oninput: set('description'),
              }),
              'inc-desc',
            ),
          ),

          kind?.asksPeople
            ? h(
                'div',
                { class: 'dlg__subgroup' },
                field(
                  'People involved',
                  h('input', {
                    id: 'inc-people',
                    class: 'input',
                    type: 'number',
                    min: '0',
                    step: '1',
                    inputmode: 'numeric',
                    value: d.peopleInvolved,
                    oninput: set('peopleInvolved'),
                  }),
                  'inc-people',
                ),
                kind.asksDaysLost
                  ? field(
                      'Days lost',
                      h('input', {
                        id: 'inc-days',
                        class: 'input',
                        type: 'number',
                        min: '0',
                        step: 'any',
                        inputmode: 'decimal',
                        value: d.daysLost,
                        oninput: set('daysLost'),
                      }),
                      'inc-days',
                    )
                  : null,
                h(
                  'div',
                  { style: 'grid-column: 1 / -1' },
                  field(
                    optionalLabel('Who'),
                    h('input', {
                      id: 'inc-names',
                      class: 'input',
                      placeholder: 'Names or roles',
                      value: d.peopleNames,
                      oninput: set('peopleNames'),
                    }),
                    'inc-names',
                  ),
                ),
              )
            : null,

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              optionalLabel('What was done about it'),
              h('textarea', {
                id: 'inc-action',
                class: 'input',
                rows: 2,
                value: d.action,
                oninput: set('action'),
              }),
              'inc-action',
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            sitePhotoField(
              d.photo,
              false,
              (url) => actions.setIncidentPhoto(url),
              (msg) => actions.setSiteFormError('incident', msg),
            ),
          ),
        ),
      ),

      dialogFoot(
        () => actions.closeIncident(),
        s.incidentSaving ? 'Reporting…' : kind ? `Report ${kind.reportAs}` : 'Report',
        s.incidentSaving,
      ),
    );
  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeIncident(), () => s.incidentSaving) },
    form,
  );
}

/* ===========================================================================
   A DOCUMENT
   =========================================================================== */

export function documentDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.documentBridgeId) return null;
  const b = findBridge(s.documentBridgeId);
  if (b === null) return null;

  const d = s.documentDraft;
  const set = (key: keyof DocumentDraft) => (e: Event) =>
    actions.setDocumentField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeDocument(), () => s.documentSaving) },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `File a document — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveDocument();
        },
      },

      dialogHead(`${b.id} · document`, 'File a document', () => actions.closeDocument()),

      s.documentError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.documentError }))
        : null,

      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__form-grid' },

          h('div', { style: 'grid-column: 1 / -1' }, documentFileField(d)),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Kind',
              h(
                'select',
                { id: 'doc-kind', class: 'select', onchange: set('kind') },
                h('option', { value: '', selected: d.kind === '', text: 'Choose…' }),
                ...DOCUMENT_KINDS.map((k) =>
                  h('option', { value: k.id, selected: k.id === d.kind, text: k.label }),
                ),
              ),
              'doc-kind',
            ),
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Title',
              h('input', {
                id: 'doc-title',
                class: 'input',
                required: true,
                /* Suggested from the file name when one is chosen — see
                   setDocumentFile. A required field somebody has to retype
                   from the thing they just picked gets filled with "scan". */
                placeholder: 'What somebody looking for this in two years would search for',
                value: d.title,
                oninput: set('title'),
              }),
              'doc-title',
            ),
          ),

          field(
            'Date on the document',
            h('input', {
              id: 'doc-date',
              class: 'input',
              type: 'date',
              /* NOT defaulted to today. The date of the test is not the date
                 of the upload, and a form that filled it in would put today on
                 every certificate in the register. */
              placeholder: 'Often absent',
              value: d.datedOn,
              oninput: set('datedOn'),
            }),
            'doc-date',
          ),

          field(
            'Against an entry',
            h(
              'select',
              { id: 'doc-event', class: 'select', onchange: set('eventId') },
              h('option', { value: '', selected: d.eventId === '', text: 'The structure' }),
              /* Only entries the register holds. A queued one has no id yet and
                 could not be referenced. */
              ...s.progress
                .filter((e) => !e.pending)
                .slice(0, 40)
                .map((e) =>
                  h('option', {
                    value: e.id,
                    selected: e.id === d.eventId,
                    text: `${e.occurredOn} · ${activityLabel(e.activity)}`,
                  }),
                ),
            ),
            'doc-event',
          ),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Note',
              h('textarea', {
                id: 'doc-note',
                class: 'input',
                rows: 2,
                value: d.note,
                oninput: set('note'),
              }),
              'doc-note',
            ),
          ),
        ),

        h('div', {
          class: 'dlg__hint',
          text: 'PDFs and images, up to 8 MB.',
        }),
      ),

      dialogFoot(
        () => actions.closeDocument(),
        s.documentSaving ? 'Filing…' : 'File it',
        s.documentSaving,
      ),
    ),
  );
}

/**
 * Choosing the file, and reading it without shrinking it.
 *
 * NOT `downscale`, which every photograph in this application goes through. A
 * certificate is read for the figures on it and a scan re-encoded to fit a
 * thumbnail budget is a certificate nobody can read. The size is checked
 * instead, and refused with the number, so the person knows what to do about
 * it rather than watching an upload fail.
 */
function documentFileField(d: DocumentDraft): HTMLElement {
  const input = h('input', {
    type: 'file',
    accept: '.pdf,image/*',
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      target.value = '';
      if (!file) return;

      if (!isDocumentType(file.type)) {
        actions.setDocumentError(
          `This register files PDFs and images. "${file.type || 'that file'}" is neither.`,
        );
        return;
      }
      if (file.size > MAX_DOCUMENT_BYTES) {
        actions.setDocumentError(
          `That file is ${Math.round(file.size / 1024)} kB, over the ` +
            `${Math.round(MAX_DOCUMENT_BYTES / 1024 / 1024)} MB limit.`,
        );
        return;
      }

      const reader = new FileReader();
      reader.onerror = () => actions.setDocumentError('That file could not be read.');
      reader.onload = () => {
        const url = typeof reader.result === 'string' ? reader.result : '';
        if (url === '') {
          actions.setDocumentError('That file could not be read.');
          return;
        }
        actions.setDocumentFile({ dataUrl: url, filename: file.name, bytes: file.size });
      };
      reader.readAsDataURL(file);
    },
  });

  return field(
    'File',
    d.file
      ? h(
          'div',
          { class: 'docpick docpick--chosen' },
          h('span', { class: 'docpick__name', text: d.filename || 'The chosen file' }),
          h('span', { class: 'docpick__size', text: `${Math.round(d.bytes / 1024)} kB` }),
          h('button', {
            type: 'button',
            class: 'ctree__clear',
            text: 'Choose another',
            onclick: () => actions.setDocumentFile(null),
          }),
        )
      : h('label', { class: 'docpick' }, input, h('span', { text: 'Choose a PDF or an image' })),
  );
}

/* --------------------------- the shared chrome --------------------------- */

/** The form's own attributes: a modal sheet. */
function formAttrs(): Record<string, string> {
  return { class: 'dlg__panel dlg__panel--sm', role: 'dialog', 'aria-modal': 'true' };
}

export function dialogHead(kicker: string, title: string, close: () => void): HTMLElement {
  return h(
    'div',
    { class: 'dlg__head' },
    h(
      'div',
      {},
      h('div', { class: 'dlg__kicker', text: kicker }),
      h('h2', { class: 'dlg__title', text: title }),
    ),
    h('span', { class: 'spacer' }),
    h(
      'button',
      {
        type: 'button',
        class: 'dlg__close',
        title: 'Cancel',
        'aria-label': 'Cancel',
        onclick: close,
      },
      '×',
    ),
  );
}

export function dialogFoot(cancel: () => void, label: string, busy: boolean): HTMLElement {
  return h(
    'div',
    { class: 'dlg__foot' },
    h('span', { class: 'spacer' }),
    button('Cancel', { onClick: cancel }),
    button(label, { primary: true, type: 'submit', disabled: busy }),
  );
}

/**
 * Whether a change to one of the three drafts needs anything redrawn beyond
 * the control it landed in.
 *
 * The same rule every dialog here keeps. What derives: the incident's kind
 * (it lights a row and prints its note), the document's file and kind, and the
 * return's day — which loads a different day's figures into every other box on
 * the sheet. Everything else is drawn by its own input and costs no render.
 */
export function siteDialogsNeedPaint(
  a: { returnDraft: ReturnDraft; incidentDraft: IncidentDraft; documentDraft: DocumentDraft },
  b: { returnDraft: ReturnDraft; incidentDraft: IncidentDraft; documentDraft: DocumentDraft },
): boolean {
  return (
    a.returnDraft.returnedOn !== b.returnDraft.returnedOn ||
    /* A trade or plant row added or taken away, the total the trades make, and
       a photograph attached or removed (LC-56). */
    a.returnDraft.trades.length !== b.returnDraft.trades.length ||
    a.returnDraft.plantRows.length !== b.returnDraft.plantRows.length ||
    tradeTotal(a.returnDraft) !== tradeTotal(b.returnDraft) ||
    a.returnDraft.photo !== b.returnDraft.photo ||
    a.incidentDraft.photo !== b.incidentDraft.photo ||
    a.incidentDraft.kind !== b.incidentDraft.kind ||
    a.documentDraft.file !== b.documentDraft.file ||
    a.documentDraft.kind !== b.documentDraft.kind ||
    /* The title moves on its own when a file is chosen, so a change to it that
       did NOT come from typing has to repaint. Comparing it costs one string
       compare and saves a box that shows the old title. */
    a.documentDraft.title !== b.documentDraft.title
  );
}
